import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import DashboardWrapper from "../../../components/DashboardWrapper";
import CrudTable from "../../../components/dashboard/table/CrudTable";
import productService from "../../../services/productService";

export default function ReadProductReviews() {
  const navigate = useNavigate();
  const { productId } = useParams();

  const CACHE_KEY = `product_reviews_cache_${productId}`;
  const columns = ["sl", "username", "email", "phone_number", "rating", "review", "status"];

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // ===== ALERT HELPER =====
  const showAlert = useCallback((title, text, icon) => {
    Swal.fire({
      title,
      text,
      icon,
      confirmButtonText: "OK",
      customClass: { confirmButton: "btn btn-primary" },
      buttonsStyling: false,
    });
  }, []);

  // ===== LOAD REVIEWS =====
  const loadReviews = useCallback(async () => {
    if (!productId) return;

    try {
      setLoading(true);

      const response = await productService.getProductReviews(productId);
      console.log("API Response:", response);

      const apiData = response?.data?.data || response?.data || [];

      const formatted = apiData.map((item, index) => ({
        id: item.id,
        sl: index + 1,
        username: item.user?.username || "-",
        email: item.user?.email || "-",
        phone_number: item.user?.phone_number || "-",
        rating: item.rating || 0,
        review: item.review || "-",
        status: item.status ?? 0,
      }));

      setReviews(formatted);
      localStorage.setItem(CACHE_KEY, JSON.stringify(formatted));
    } catch (error) {
      console.error("Load Reviews Error:", error.response || error);
      showAlert("Error", "Failed to load product reviews", "error");
    } finally {
      setLoading(false);
    }
  }, [productId, CACHE_KEY, showAlert]);

  // ===== LOAD FROM CACHE FIRST =====
  useEffect(() => {
    try {
      const cache = localStorage.getItem(CACHE_KEY);
      if (cache) {
        setReviews(JSON.parse(cache));
        setLoading(false);
      } else {
        loadReviews(); // Call API only if no cache
      }
    } catch {
      localStorage.removeItem(CACHE_KEY);
      loadReviews();
    }
  }, [CACHE_KEY, loadReviews]);

  // ===== TOGGLE REVIEW STATUS =====
  const handleToggleStatus = useCallback(
    async (item) => {
      try {
        const newStatus = item.status ? 0 : 1;

        // Call backend
        const res = await productService.changeProductReviewStatus(item.id, newStatus);
        console.log("Toggle Status Response:", res);

        // Check backend response structure (adjust according to your API)
        const success =
          res?.data?.success !== undefined
            ? res.data.success
            : res?.status === 200; // fallback if no success field

        if (!success) {
          showAlert("Error", "Review status not updated on server.", "error");
          return;
        }

        // Update state & cache
        setReviews((prev) => {
          const updated = prev.map((review) =>
            review.id === item.id ? { ...review, status: newStatus } : review
          );
          localStorage.setItem(CACHE_KEY, JSON.stringify(updated));
          return updated;
        });

        showAlert("Success!", "Review status updated.", "success");
      } catch (error) {
        console.error("Status update failed:", error.response || error);
        showAlert("Error", "Failed to update review status.", "error");
      }
    },
    [CACHE_KEY, showAlert]
  );

  return (
    <DashboardWrapper>
      <div className="container-fluid mt-4">
        <button className="btn btn-secondary mb-3" onClick={() => navigate(-1)}>
          Back
        </button>

        <div className="card shadow">
          <div className="card-header">
            <h4>Product Reviews</h4>
          </div>

          <div className="card-body">
            {loading ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" />
              </div>
            ) : (
              <CrudTable
                columns={columns}
                data={reviews}
                onToggleStatus={handleToggleStatus}
              />
            )}
          </div>
        </div>
      </div>
    </DashboardWrapper>
  );
}
